<template>
  <div class="locations ds-panel" :class="cssClass">
    <div class="ds-panel__head">
      <h3
        class="ds-panel__title"
        :class="{
          'text-danger': store.error && store.requireFields?.includes('locations'),
        }"
      >
        {{ t('measurement-location').toUpperCase() }}
      </h3>

      <div
          v-if="cssClass !== 'vms' && sortedLocations.length"
          class="ds-panel__aside select-all-container"
      >
        <span class="select-all-text">
          {{ allSelected ? t('unmark') : t('mark') }} {{ t('all').toLowerCase() }}
        </span>

        <!-- Slot o szerokości kolumny „Wybierz" — checkbox „zaznacz wszystkie"
             stoi dokładnie w jednej osi z checkboxami wierszy. -->
        <span class="select-all-slot">
          <input-checkbox
              class="select-all-checkbox"
              field-name="selectAll"
              field-value=""
              :checkedCondition="allSelected"
              @clicked="setAllFilter()"
          />
        </span>
      </div>
    </div>

    <div class="ds-table-scroll">
    <table class="table">
      <thead>
        <tr>
          <!-- Kolumna statusu dostępności — bez nagłówka (ikona mówi sama za siebie). -->
          <th class="status-col" scope="col"></th>
          <th scope="col">Id</th>
          <th scope="col">{{ t('location-name') }}</th>
          <th scope="col">{{ t('road-no') }}</th>
          <th scope="col">{{ t('chainage') }}</th>
          <th scope="col" class="select-col" ref="selectColHeader">{{ t('select') }}</th>
        </tr>
      </thead>

      <tbody v-if="sortedLocations.length">
        <tr v-for="loc in sortedLocations" :key="loc.id">
          <td class="status-col">
            <!-- Ikona dostępności widoczna dla każdego. Link do historii statusów
                 (klik) tylko dla adminów (ROLE_5 lub 6) i userów z dostępem do logów
                 aktywności (ROLE_7) — pozostałych router guard i tak by odrzucił. -->
            <button
              v-if="canSeeStatus"
              type="button"
              class="status-link"
              @click.stop="goToLatestStatus(loc.id)"
              :title="t('location-statuses-history')"
            >
              <img :src="isActive(loc) ? availableIcon : unavailableIcon" :alt="t('status')" />
            </button>
            <img
              v-else
              :src="isActive(loc) ? availableIcon : unavailableIcon"
              :alt="t('status')"
            />
          </td>
          <td>{{ loc.id }}</td>
          <td>{{ loc.shortName }}</td>
          <td>{{ loc.roadNumber }}</td>
          <td>{{ loc.chainage }}</td>
          <td class="check">
            <input-checkbox
              :field-name="String(loc.id)"
              field-value=""
              :checkedCondition="isChecked(loc.id)"
              @clicked="setFilter(loc.id)"
            />
          </td>
        </tr>
      </tbody>

      <tbody v-else>
        <tr>
          <td colspan="6">
            <p class="no-data center-aligned">{{ t('no-data') }}</p>
          </td>
        </tr>
      </tbody>
    </table>
    </div>
  </div>
</template>

<script>
import InputCheckbox from "../InputCheckbox.vue";
import availableIcon from "@/assets/icons/dostepny.png";
import unavailableIcon from "@/assets/icons/niedostepny.png";
import { isSG } from "@/config/appVersion";
import FiltersService from "@/services/FiltersService";
import { useUserStore } from "@/stores/user.store";

export default {
  name: "LocationList",
  components: { InputCheckbox },
  setup() {
    // Status lokalizacji (kolumna z ikoną dostępny/niedostępny + link do historii statusów)
    // jest widoczny wyłącznie dla administratorów (klucz `LocationStatuses` → ROLE_5 lub 6).
    return { userStore: useUserStore() };
  },
  props: {
    store: { required: true },
    cssClass: String,

    // ✅ parametr "po jakim czasie offline" (minuty)
    // 8h = 480 minut
    freshnessThresholdMinutes: { type: Number, default: isSG ? 480 : 30 },

    // jak często odświeżać statusy z backendu (ms)
    // 5 minut: nie podtrzymuje sztucznie aktywności sesji przy krótkich timeoutach bezczynności
    statusPollIntervalMs: { type: Number, default: 5 * 60 * 1000 },

    // jeśli backend zwraca unix seconds, ustaw na true
    lastTimeIsUnixSeconds: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      error: null,

      // reaktywne "tyknięcie" czasu dla przerysowania statusów
      tick: 0,
      timer: null,
      hasBeenActivated: false,
      refreshInFlight: null,

      // obserwator szerokości kolumny „Wybierz" (wyrównanie checkboxa „zaznacz wszystkie")
      selectColObserver: null,
      selectColRaf: null,
      selectColWidth: null,

      availableIcon,
      unavailableIcon,
    };
  },

  async created() {
    // ✅ od razu przy wejściu pobierz świeże lokalizacje + przerysuj statusy
    await this.refreshNow({ force: true });
  },

  mounted() {
    this.startAutoRefresh();
    this.observeSelectColumn();
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    window.addEventListener("focus", this.onWindowFocus);
  },

  async activated() {
    this.startAutoRefresh();

    // Przy keep-alive: odświeżaj dane za każdym kolejnym wejściem na komponent.
    if (this.hasBeenActivated) {
      await this.refreshNow({ force: true });
    }
    this.hasBeenActivated = true;
  },

  deactivated() {
    this.stopAutoRefresh();
  },

  beforeUnmount() {
    this.stopAutoRefresh();
    this.selectColObserver?.disconnect();
    this.selectColObserver = null;
    if (this.selectColRaf !== null) {
      cancelAnimationFrame(this.selectColRaf);
      this.selectColRaf = null;
    }
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    window.removeEventListener("focus", this.onWindowFocus);
  },

  watch: {
    "store.locations"(newVal) {
      this.loading = !newVal || newVal.length === 0;
    },
  },

  computed: {
    sortedLocations() {
      const arr = Array.isArray(this.store.locations) ? this.store.locations : [];
      return [...arr].sort((a, b) => Number(a.id) - Number(b.id));
    },
    selectedIdSet() {
      const ids = this.store?.filters?.locations || [];
      return new Set(ids.map((x) => Number(x)));
    },
    allSelected() {
      const locs = this.sortedLocations;
      if (!locs.length) return false;
      return locs.every((l) => this.selectedIdSet.has(Number(l.id)));
    },
    thresholdMs() {
      return this.freshnessThresholdMinutes * 60 * 1000;
    },
    canSeeStatus() {
      // Klikalność ikony (link do historii statusów lokalizacji). Sama ikona
      // dostępności jest widoczna dla wszystkich — tu decydujemy tylko o linku.
      // ROLE_5 = administrator (LocationStatuses), ROLE_6 = pełny dostęp do logów
      // aktywności użytkowników.
      return this.userStore.checkRole('ROLE_5') || this.userStore.checkRole('ROLE_6');
    },
  },

  methods: {
    /**
     * Checkbox „zaznacz wszystkie" w nagłówku panelu musi stać w jednej osi pionowej
     * z checkboxami wierszy. Faktyczna szerokość kolumny „Wybierz" nie jest znana z CSS:
     * zależy od tłumaczenia nagłówka (pl „Wybierz", de „Auswählen") i od tego, ile luzu
     * rozdzieli layout tabeli. Dlatego mierzymy ją i przekazujemy do CSS zmienną
     * `--location-select-col`, z której korzysta slot w nagłówku.
     *
     * Mierzymy `offsetWidth`, NIE `getBoundingClientRect().width`. Interfejs startuje
     * przeskalowany (`--ui-scale` w design-system.css), a rect zwraca szerokość już
     * po przeskalowaniu — zapisanie jej z powrotem jako `px` rozjeżdżałoby kolumnę
     * o współczynnik skali przy każdym odczycie i ResizeObserver nigdy by się nie
     * ustabilizował („ResizeObserver loop completed with undelivered notifications").
     * `offsetWidth` jest w układzie współrzędnych samego elementu, czyli w tym samym,
     * w którym CSS zinterpretuje zapisaną wartość.
     */
    syncSelectColumnWidth() {
      const th = this.$refs.selectColHeader;
      if (!th || !this.$el?.style) return;

      const width = th.offsetWidth;
      if (width <= 0) return;

      // Zapis tylko przy realnej zmianie — inaczej każda notyfikacja obserwatora
      // wywoływałaby kolejny zapis, a ten kolejną notyfikację.
      if (this.selectColWidth !== null && Math.abs(width - this.selectColWidth) < 0.5) {
        return;
      }

      this.selectColWidth = width;
      this.$el.style.setProperty("--location-select-col", `${width}px`);
    },

    observeSelectColumn() {
      const th = this.$refs.selectColHeader;
      if (!th || typeof ResizeObserver === "undefined") {
        this.syncSelectColumnWidth();
        return;
      }

      // Zapis do stylu wykonujemy w następnej klatce, poza cyklem obserwacji —
      // modyfikacja layoutu wprost w callbacku ResizeObservera jest źródłem
      // ostrzeżenia o niedostarczonych notyfikacjach.
      this.selectColObserver = new ResizeObserver(() => {
        if (this.selectColRaf !== null) return;
        this.selectColRaf = requestAnimationFrame(() => {
          this.selectColRaf = null;
          this.syncSelectColumnWidth();
        });
      });
      this.selectColObserver.observe(th);
      this.syncSelectColumnWidth();
    },

    onVisibilityChange() {
      if (!document.hidden) {
        this.refreshNow({ force: true });
      }
    },

    onWindowFocus() {
      this.refreshNow({ force: true });
    },

    startAutoRefresh() {
      this.stopAutoRefresh();
      this.timer = setInterval(async () => {
        await this.refreshNow({ force: true });
      }, this.statusPollIntervalMs);
    },

    stopAutoRefresh() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },

    async fetchFreshLocations() {
      const data = await FiltersService.getLocations();
      if (typeof this.store?.$patch === "function") {
        const patch = { locations: Array.isArray(data) ? data : [] };

        if (Array.isArray(this.store.laneNumbers)) {
          const lanesSet = new Set();
          patch.locations.forEach((location) => {
            const laneCount = Array.isArray(location?.lanes) ? location.lanes.length : 0;
            for (let i = 1; i <= laneCount; i++) {
              lanesSet.add(i);
            }
          });
          patch.laneNumbers = [...lanesSet].sort((a, b) => a - b);
        }

        this.store.$patch(patch);
        return;
      }

      await this.store.getLocations?.();
    },

    async refreshNow({ force = false } = {}) {
      if (this.refreshInFlight) {
        await this.refreshInFlight;
        return;
      }

      this.refreshInFlight = (async () => {
        if (force) {
          try {
            await this.fetchFreshLocations();
          } catch (e) {
            await this.store.getLocations?.();
          }
        } else {
          await this.store.getLocations?.();
        }
        this.tick++;
      })();

      try {
        await this.refreshInFlight;
      } finally {
        this.refreshInFlight = null;
      }
    },

    setFilter(id) {
      this.store.addItem?.("LocationList", Number(id));
    },
    isChecked(id) {
      return this.selectedIdSet.has(Number(id));
    },
    setAllFilter() {
      this.store.checkAllLocations?.();
    },
    goToLatestStatus(locationId) {
      this.$router.push({ name: "LocationStatusLatestView", params: { id: Number(locationId) } });
    },

    parseTimestamp(value) {
      if (value == null) return NaN;

      if (typeof value === "number") {
        return this.lastTimeIsUnixSeconds ? value * 1000 : value;
      }

      const trimmed = String(value).trim();

      // liczba w stringu
      if (/^\d+$/.test(trimmed)) {
        const n = Number(trimmed);
        return this.lastTimeIsUnixSeconds ? n * 1000 : n;
      }

      // ISO / date string
      const ms = Date.parse(trimmed);
      return Number.isNaN(ms) ? NaN : ms;
    },

    isActive(loc) {
      // ✅ zależność od tick = wymusza reaktywność
      void this.tick;

      const ts = this.parseTimestamp(loc?.lastVehicleTime);
      if (Number.isNaN(ts)) return false;

      return Date.now() - ts <= this.thresholdMs;
    },
  },
};
</script>

<style scoped>
/* Panel „Miejsce pomiaru" — tabela wypełnia całą szerokość panelu, bez
   dodatkowego paddingu (obramowania daje sam panel z design-system.css). */
.locations {
  min-width: 0;

  /* Szerokości kolumn skrajnych. `--location-select-col` to wartość startowa — po
     zamontowaniu komponent nadpisuje ją realną szerokością kolumny „Wybierz"
     (patrz `syncSelectColumnWidth`), żeby checkbox „zaznacz wszystkie" leżał
     dokładnie w jednej osi z checkboxami wierszy. */
  --location-select-col: 46px;
  --location-status-col: 44px;

  /* Wysokość wiersza lokalizacji — stała, więc każda lokalizacja ma dokładnie tyle samo
     miejsca niezależnie od tego, ile ich jest. 50 px odpowiada wierszowi w pozostałych
     panelach filtrów (`ViolationList`: 14 px + treść + 14 px + linia ≈ 51 px). */
  --location-row-height: 50px;
}

/* tekst "zaznacz wszystkie" + checkbox po PRAWEJ stronie nagłówka.
   Ujemny margines zdejmuje poziomy padding `.ds-panel__head`, żeby prawa krawędź
   slotu pokryła się z prawą krawędzią tabeli. */
.select-all-container {
  margin-right: calc(-1 * var(--spacing-md));
}

.select-all-text {
  font-size: var(--font-size-md);
  color: var(--text-secondary);
}

/* Slot odpowiada kolumnie „Wybierz" — checkbox centrowany tak samo jak w wierszach. */
.select-all-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--location-select-col);
  flex: 0 0 auto;
}

.select-all-container :deep(.select-all-checkbox.container),
.select-all-container :deep(.select-all-checkbox.container2) {
  margin: 0;
  transform: none;
}

/* TABELA
   Tabela NIE rozciąga się na całą wysokość panelu — wiersze mają stałą wysokość
   (`--location-row-height`), a nadmiar wysokości zostaje pustym miejscem pod listą.
   `margin-bottom: 0` jest konieczne: bootstrapowe `.table { margin-bottom: 1rem }` ma
   wyższą specyficzność niż `table { margin-bottom: 0 }` z design-system i zostawiało
   16 px pustego pasma pod ostatnim wierszem (ostatnia lokalizacja „dostawała" więcej). */
.locations table {
  width: 100%;
  margin-bottom: 0;
  border-collapse: collapse;
}

/* Nagłówki zostają na `--font-size-sm`: dają hierarchię wobec danych i nie rozpychają
   tabeli w węższych kolumnach layoutu (archiwum min. 380 px, statystyki min. 340 px). */
.locations.ds-panel table thead th {
  padding: var(--spacing-sm) var(--spacing-xs);
  border-bottom: 1px solid var(--border-default);
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-regular);
  text-align: center;
  vertical-align: middle;
  /* design-system wymusza `nowrap` na nagłówkach tabel; tutaj pozwalamy im się zawinąć
     („Numer drogi", „Nazwa miejsca"), żeby wąskie kolumny layoutu nie generowały
     poziomego scrolla. */
  white-space: normal;
}

/* Dane czytelniejsze (15 px) i wiersze o stałej, jednakowej wysokości — nieważne, czy
   w kolumnie statusu jest ikona-link (ROLE_5/ROLE_6) czy sama ikona. */
.locations.ds-panel table tbody td {
  height: var(--location-row-height);
  padding: var(--spacing-sm) var(--spacing-xs);
  border-bottom: 1px solid var(--border-subtle);
  font-size: var(--font-size-md);
  line-height: 24px;
  text-align: center;
  vertical-align: middle;
}

/* Ostatni wiersz nie dubluje linii z obramowaniem panelu, ale zachowuje tę samą
   wysokość co pozostałe (przezroczysta krawędź zamiast `border-bottom: 0`). */
.locations.ds-panel table tbody tr:last-child td {
  border-bottom-color: transparent;
}

.locations.ds-panel table tbody tr:hover {
  background: var(--surface-hover);
}

.locations td.check span {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
}

.locations th.select-col,
.locations td.check {
  width: var(--location-select-col);
}

/* Kolumna statusu — ikony odsunięte od lewej krawędzi panelu, żeby nie „kleiły się"
   do obramowania. Zawartość wyrównana do lewej, bo padding ma być widoczny. */
.locations.ds-panel table thead th.status-col,
.locations.ds-panel table tbody td.status-col {
  width: var(--location-status-col);
  padding-left: var(--spacing-md);
  padding-right: 0;
  text-align: left;
}

/* Ikona statusu w stałym boksie — nie rozpycha wiersza i nie zależy od rozmiaru PNG
   (pliki mają 27×27). `vertical-align: middle` zdejmuje pustą przestrzeń pod linią
   bazową, dzięki czemu wysokość wiersza wynika wyłącznie z paddingu i `line-height`. */
.locations.ds-panel table tbody td.status-col img,
.locations.ds-panel table tbody td.status-col .status-link {
  vertical-align: middle;
}

.locations.ds-panel table tbody td.status-col img {
  display: block;
  width: 24px;
  height: 24px;
}

/* Komunikat „brak danych" — `p` z Bootstrapa wnosi `margin-bottom: 1rem`, co
   dokładało puste miejsce pod tabelą. */
.locations .no-data {
  margin: 0;
}

/* Numer drogi i pikietaż to wartości techniczne — nie łamiemy ich na dwie linie,
   bo tracą czytelność. Nagłówki (np. „Numer drogi") mogą się zawinąć i dzięki temu
   tabela mieści się bez poziomego scrolla także w najwęższych kolumnach layoutu. */
.locations.ds-panel table tbody td {
  white-space: nowrap;
}

/* wyjątek: nazwa miejsca może się zawinąć */
.locations.ds-panel table tbody td:nth-child(3) {
  white-space: normal;
  overflow-wrap: anywhere;
}

/* Ikona statusu jako link do historii — bez wizualnej otoczki przycisku. */
.status-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 0;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
}

.status-link:hover {
  opacity: 0.75;
}
</style>
